/**
 * The statutory forms on file (docs/ux/templates.md addendum, D-0055): the
 * nine WorkCover WA permanent impairment forms with the version, file and
 * shape of each, a download, and an upload that adds a version. The three
 * the portal fills say so; a synthetic stand-in says it is one. The phone
 * reads; uploads are a desktop task.
 */

import { useQueryClient } from '@tanstack/react-query';
import { DownloadIcon } from 'lucide-react';
import { toast } from 'sonner';
import { say } from '@/app/announcer';
import type { FormCode, FormRecord } from '@/app/types';
import { useIsPhone } from '@/app/viewport';
import { ErrorPanel } from '@/components/shell/states';
import { Skeleton } from '@/components/ui/skeleton';
import { dateTime } from '@/lib/format';
import { UploadField } from '../UploadField';
import { formsKey, uploadForm, useForms } from '../useRegistry';

const DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const MAX_BYTES = 20 * 1024 * 1024;
const FILLED: readonly FormCode[] = ['APIA1', 'APIA6', 'APIA7'];

export const formLabel = (f: FormRecord): string => (f.code.startsWith('APIA') ? `${f.code}: ${f.title}` : f.title);

export function FormsPanel({ offline }: { offline: boolean }) {
  const phone = useIsPhone();
  const client = useQueryClient();
  const forms = useForms();
  const heading = <h2 id="forms-heading" className="eyebrow text-beam-text-muted">Statutory forms{forms.data ? <span className="tabular ml-2 font-normal normal-case tracking-normal">{forms.data.filter((f) => f.assetKey).length} of {forms.data.length} on file</span> : null}</h2>;
  const shell = (body: React.ReactNode) => (
    <section aria-labelledby="forms-heading" data-slot="forms-panel" className="flex flex-col gap-3 rounded-xl border border-beam-line bg-beam-paper p-4">
      {heading}
      <p className="text-[13px] text-beam-text-muted">The WorkCover WA permanent impairment forms are filled, never authored. APIA1, APIA6 and APIA7 are the ones a permanent impairment matter fills; the rest are on file for the coordinator. When WorkCover WA revises a form, upload the official Word file: a new version is added and earlier versions are kept.</p>
      {body}
    </section>
  );
  if (forms.isError) return shell(<ErrorPanel message="The statutory forms could not be loaded." onRetry={() => void forms.refetch()} />);
  if (forms.isPending) return shell(<Skeleton className="h-40 rounded-lg" aria-label="Loading the statutory forms" />);

  function landed(record: FormRecord) {
    client.setQueryData<FormRecord[]>(formsKey, (old) => (old ?? []).map((f) => (f.code === record.code ? record : f)));
    toast.success(`${record.code} version ${record.fileVersion} on file.`);
    say(`${record.code} version ${record.fileVersion} on file; it is the form the next fill uses.`);
    void client.invalidateQueries({ queryKey: ['registry', 'doctors'] });
  }

  return shell(
    <ul data-slot="form-list" className="flex flex-col divide-y divide-beam-line text-sm">
      {forms.data.map((f) => (
        <li key={f.code} data-slot="form-row" data-form={f.code} data-on-file={f.assetKey ? 'true' : 'false'} className="flex flex-col gap-2 py-3 md:flex-row md:items-start md:justify-between md:gap-6">
          <div className="flex min-w-0 flex-col gap-0.5 md:flex-1">
            <span className="font-medium">{formLabel(f)}{FILLED.includes(f.code) ? <span className="ml-2 rounded-full bg-beam-cream-deep px-2 py-0.5 text-[11px] font-medium text-beam-text-muted">filled by the portal</span> : null}</span>
            <span className="text-[13px] text-beam-text-muted" data-slot="form-status">
              {f.assetKey
                ? <>Version <span className="tabular">{f.fileVersion}</span>{f.synthetic ? ', a synthetic stand-in' : ''}: {f.fileName}, <span className="tabular">{f.controls}</span> controls in <span className="tabular">{f.tables}</span> tables.{f.uploadedAt ? ` Added ${dateTime(f.uploadedAt)}${f.uploadedBy ? ` by ${f.uploadedBy}` : ''}.` : ''}</>
                : FILLED.includes(f.code) ? 'Not on file. Permanent impairment cases cannot generate until APIA1 and APIA6 are on file.' : 'Not on file.'}
            </span>
            {f.assetKey ? (
              <a href={`/api/registry/forms/${f.code}/file`} download={f.fileName ?? `${f.code}.docx`} data-slot="download-form" className="inline-flex w-fit items-center gap-1.5 text-[13px] underline-offset-2 hover:underline">
                <DownloadIcon aria-hidden="true" className="size-3.5" />Download the file on file
              </a>
            ) : null}
          </div>
          {phone ? null : (
            <div className="md:w-[380px] md:shrink-0">
              <UploadField<FormRecord>
                send={(file, onProgress, signal) => uploadForm(f.code, file, onProgress, signal)}
                label={`${f.code} form`}
                accept={{ mime: [DOCX], extensions: ['.docx'], words: 'a Word document' }}
                maxBytes={MAX_BYTES}
                offline={offline}
                current={f.assetKey ? { name: 'On file' } : null}
                onLanded={landed}
                compact
                help="The official Word file from WorkCover WA, up to 20 MB; it must carry content controls. A new version is added; earlier versions are kept."
              />
            </div>
          )}
        </li>
      ))}
    </ul>,
  );
}
